import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../environments/environment';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private apiUrl = environment.apiUrl;
    
    private currentUserSubject: BehaviorSubject<any>;
    public currentUser: Observable<any>;

    constructor(private http: HttpClient) {
        this.currentUserSubject = new BehaviorSubject<any>(JSON.parse(localStorage.getItem('currentUser') || 'null'));
        this.currentUser = this.currentUserSubject.asObservable();
    }

    public getCurrentUserValue(): any {
        return this.currentUserSubject.value;
    }

    login(usernameOrFormData: string | FormData, password?: string): Observable<any> {
        if (usernameOrFormData instanceof FormData) {
            return this.http.post<any>(`${this.apiUrl}/token`, usernameOrFormData);
        } else {
            const formData = new FormData();
            formData.append('username', usernameOrFormData);
            if (password) {
                formData.append('password', password);
            }
            return this.http.post<any>(`${this.apiUrl}/token`, formData);
        }
    }

    logout(): Observable<any> {
        return this.http.post<any>(`${this.apiUrl}/logout`, {});
    }

    getCurrentUser(): Observable<any> {
        return this.http.get<any>(`${this.apiUrl}/users/me`);
    }

    fetchCurrentUser(): Observable<any> {
        return this.getCurrentUser();
    }

    isAuthenticated(): Observable<boolean> {
        return this.getCurrentUser().pipe(
            map(res => {
                this.setCurrentUserValue(res);
                return true;
            }),
            catchError(() => {
                localStorage.removeItem('currentUser');
                this.currentUserSubject.next(null);
                return of(false);
            })
        );
    }

    setCurrentUserValue(user: any): void {
        localStorage.setItem('currentUser', JSON.stringify(user));
        this.currentUserSubject.next(user);
    }

    getUsers(skip: number = 0, limit: number = 100): Observable<any[]> {
        return this.http.get<any[]>(`${this.apiUrl}/users`, { params: { skip, limit } });
    }

    getAllUsers(skip: number = 0, limit: number = 100): Observable<any[]> {
        return this.getUsers(skip, limit);
    }

    registerUser(userData: any): Observable<any> {
        return this.http.post<any>(`${this.apiUrl}/register`, userData);
    }

    register(userData: any): Observable<any> {
        return this.registerUser(userData);
    }

    updateUser(userId: number, userData: any): Observable<any> {
        return this.http.put<any>(`${this.apiUrl}/users/${userId}`, userData);
    }

    deleteUser(userId: number): Observable<any> {
        return this.http.delete<any>(`${this.apiUrl}/users/${userId}`);
    }

    getDashboardStats(): Observable<any> {
        return this.http.get<any>(`${this.apiUrl}/dashboard/stats`);
    }

    getCreditStats(): Observable<any> {
        return this.http.get<any>(`${this.apiUrl}/credit-data/stats`);
    }

    getCreditData(
        skip: number = 0, 
        limit: number = 100, 
        search: string = '', 
        mismatch_only: boolean = false, 
        sortBy: string = 'date', 
        sortDir: string = 'desc'
    ): Observable<any[]> {
        let params: any = { skip, limit };
        if (search) {
            params.search = search;
        }
        if (mismatch_only) {
            params.mismatch_only = 'true';
        }
        params.sort_by = sortBy;
        params.sort_dir = sortDir;
        
        return this.http.get<any[]>(`${this.apiUrl}/credit-data`, { params });
    }

    updateCreditReview(id: number, payload: any): Observable<any> {
        return this.http.put<any>(`${this.apiUrl}/credit-data/${id}/review`, payload);
    }

    uploadCreditData(file: File, dataName: string): Observable<any> {
        const formData = new FormData();
        formData.append('file', file);
        if (dataName) {
            formData.append('data_name', dataName);
        }
        return this.http.post<any>(`${this.apiUrl}/credit-data/upload`, formData);
    }

    exportCreditData(
        search: string = '', 
        mismatch_only: boolean = false,
        sortBy: string = 'date',
        sortDir: string = 'desc'
    ): Observable<Blob> {
        let params: any = {};
        if (search) {
            params.search = search;
        }
        if (mismatch_only) {
            params.mismatch_only = 'true';
        }
        params.sort_by = sortBy;
        params.sort_dir = sortDir;
        
        return this.http.get(`${this.apiUrl}/credit-data/export`, {
            params,
            responseType: 'blob'
        });
    }

    getMemo(key: string): Observable<any> {
        return this.http.get<any>(`${this.apiUrl}/memos/${key}`);
    }

    updateMemo(key: string, payload: any): Observable<any> {
        return this.http.post<any>(`${this.apiUrl}/memos/${key}`, payload);
    }

    saveMemo(key: string, content: string): Observable<any> {
        return this.updateMemo(key, { content: content });
    }

    getPrompts(): Observable<any[]> {
        return this.http.get<any[]>(`${this.apiUrl}/prompts`);
    }

    getPrompt(key: string): Observable<any> {
        return this.http.get<any>(`${this.apiUrl}/prompts/${key}`);
    }

    updatePrompt(key: string, payload: any): Observable<any> {
        return this.http.post<any>(`${this.apiUrl}/prompts/${key}`, payload);
    }

    requestPasswordReset(payload: any): Observable<any> {
        return this.http.post<any>(`${this.apiUrl}/password-reset-request`, payload);
    }

    confirmPasswordReset(tokenOrPayload: string | any, new_password?: string): Observable<any> {
        if (typeof tokenOrPayload === 'string') {
            const payload = { token: tokenOrPayload, new_password: new_password };
            return this.http.post<any>(`${this.apiUrl}/password-reset-confirm`, payload);
        } else {
            return this.http.post<any>(`${this.apiUrl}/password-reset-confirm`, tokenOrPayload);
        }
    }

    getUploadStatus(batchId: string) {
        return this.http.get(`${this.apiUrl}/credit-data/upload-status/${batchId}`);
    }

    uploadCreditReviewExcel(file: File): Observable<any> {
        const formData = new FormData();
        formData.append('file', file);
        return this.http.post<any>(`${this.apiUrl}/credit-data/bulk-review`, formData);
    }

    deleteCreditData(id: number): Observable<any> {
        return this.http.delete<any>(`${this.apiUrl}/credit-data/${id}`);
    }

    getCreditStatsGemini35(): Observable<any> {
        return this.http.get<any>(`${this.apiUrl}/credit-data-gemini35/stats`);
    }

    getCreditDataGemini35(
        skip: number = 0, 
        limit: number = 100, 
        search: string = '', 
        mismatch_only: boolean = false, 
        sortBy: string = 'date', 
        sortDir: string = 'desc'
    ): Observable<any[]> {
        let params: any = { skip, limit };
        if (search) {
            params.search = search;
        }
        if (mismatch_only) {
            params.mismatch_only = 'true';
        }
        params.sort_by = sortBy;
        params.sort_dir = sortDir;
        
        return this.http.get<any[]>(`${this.apiUrl}/credit-data-gemini35`, { params });
    }

    updateCreditReviewGemini35(id: number, payload: any): Observable<any> {
        return this.http.put<any>(`${this.apiUrl}/credit-data-gemini35/${id}/review`, payload);
    }

    uploadCreditDataGemini35(file: File, dataName: string): Observable<any> {
        const formData = new FormData();
        formData.append('file', file);
        if (dataName) {
            formData.append('data_name', dataName);
        }
        return this.http.post<any>(`${this.apiUrl}/credit-data-gemini35/upload`, formData);
    }

    exportCreditDataGemini35(
        search: string = '', 
        mismatch_only: boolean = false,
        sortBy: string = 'date',
        sortDir: string = 'desc'
    ): Observable<Blob> {
        let params: any = {};
        if (search) {
            params.search = search;
        }
        if (mismatch_only) {
            params.mismatch_only = 'true';
        }
        params.sort_by = sortBy;
        params.sort_dir = sortDir;
        
        return this.http.get(`${this.apiUrl}/credit-data-gemini35/export`, {
            params,
            responseType: 'blob'
        });
    }

    getUploadStatusGemini35(batchId: string) {
        return this.http.get(`${this.apiUrl}/credit-data-gemini35/upload-status/${batchId}`);
    }

    uploadCreditReviewExcelGemini35(file: File): Observable<any> {
        const formData = new FormData();
        formData.append('file', file);
        return this.http.post<any>(`${this.apiUrl}/credit-data-gemini35/bulk-review`, formData);
    }

    deleteCreditDataGemini35(id: number): Observable<any> {
        return this.http.delete<any>(`${this.apiUrl}/credit-data-gemini35/${id}`);
    }
}